import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString("nb-NO", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

function HostChip({ h, kind }) {
  const color = kind === "new" ? "#10b981" : kind === "lost" ? "#ff8a8a" : "#a3acb9";
  const sign = kind === "new" ? "+" : kind === "lost" ? "−" : "=";
  return (
    <span
      data-testid={`diff-host-${kind}`}
      className="inline-flex items-center gap-1 px-2 py-0.5 border font-term text-[11.5px] mr-1.5 mb-1.5"
      style={{ borderColor: color, color: kind === "kept" ? "#dbe2ea" : color, fontWeight: h.self ? 700 : 400 }}
    >
      {sign} {h.host}{h.self && <span className="ml-1 text-[9px] tracking-[0.15em] uppercase">self</span>}
    </span>
  );
}

function VisibilityChart({ batches }) {
  const data = batches.map((b) => ({ date: fmtDate(b.batch_ts), score: Math.round(b.self_ratio * 100), self: b.self_citations, cites: b.citations }));
  return (
    <div data-testid="visibility-chart" className="h-48 mt-3">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 28, left: -16, bottom: 0 }}>
          <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
          <XAxis dataKey="date" stroke="#a3acb9" fontSize={11} padding={{ left: 16, right: 24 }} />
          <YAxis stroke="#a3acb9" fontSize={11} unit="%" />
          <Tooltip contentStyle={{ background: "#0b0f14", border: "1px solid #334155", fontSize: 12 }}
            formatter={(v, n, p) => [`${v}% (${p.payload.self}/${p.payload.cites})`, "Self-citation"]} />
          <Line type="monotone" dataKey="score" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function BatchSelect({ value, onChange, batches, testid }) {
  return (
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="bg-surface border border-edge px-2 py-1.5 font-term text-[12px] text-coolwhite outline-none focus:border-nettred">
      {batches.map((b) => (
        <option key={b.batch_id} value={b.batch_id}>{fmtDate(b.batch_ts)} · {b.self_citations}/{b.citations} self</option>
      ))}
    </select>
  );
}

function QueryDiffRow({ q }) {
  const delta = q.self_b - q.self_a;
  const dColor = delta > 0 ? "#10b981" : delta < 0 ? "#ff8a8a" : "#a3acb9";
  return (
    <div data-testid="diff-query-row" className="border-t border-edge pt-4 mt-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <p className="font-term text-[13px] text-coolwhite/90 max-w-2xl">{q.query}</p>
        <span className="font-hud text-[12px] tabular-nums" style={{ color: dColor }}>
          self {q.self_a} → {q.self_b} ({delta > 0 ? "+" : ""}{delta}) · cites {q.cites_a} → {q.cites_b}
        </span>
      </div>
      {(!q.in_a || !q.in_b) && (
        <div className="font-term text-[11px] text-amber mt-1">// spørringen finnes bare i {q.in_b ? "nyeste" : "eldste"} batch</div>
      )}
      <div className="mt-2 flex flex-wrap">
        {q.new.map((h) => <HostChip key={`n-${h.host}`} h={h} kind="new" />)}
        {q.lost.map((h) => <HostChip key={`l-${h.host}`} h={h} kind="lost" />)}
        {q.kept.map((h) => <HostChip key={`k-${h.host}`} h={h} kind="kept" />)}
      </div>
    </div>
  );
}

function ReportActions({ ax, a, b, clientId }) {
  const [client, setClient] = useState("");
  const [busy, setBusy] = useState("");

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const res = await ax.get("/admin/mentions/diff/pdf", { params: { a, b, client: client || undefined, client_id: clientId || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const el = document.createElement("a");
      el.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      el.download = (cd.match(/filename="([^"]+)"/) || [])[1] || "3la-ai-visibility.pdf";
      document.body.appendChild(el);
      el.click();
      el.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Kunne ikke lage PDF.");
    } finally { setBusy(""); }
  };

  const sendAlert = async () => {
    setBusy("mail");
    try {
      const { data } = await ax.post("/admin/mentions/alert/send", null, { params: { client_id: clientId || undefined } });
      data.sent ? toast.success(`AI-varsel sendt til ${data.to}`) : toast.error(`Ikke sendt: ${data.reason}`);
    } catch (e) {
      toast.error("Kunne ikke sende varsel.");
    } finally { setBusy(""); }
  };

  const btn = "font-display tracking-[0.18em] text-[11px] uppercase px-3 py-1.5 border transition-colors disabled:opacity-50";
  return (
    <div className="mt-4 flex items-center gap-2 flex-wrap">
      <input
        data-testid="diff-client-name"
        value={client}
        onChange={(e) => setClient(e.target.value)}
        placeholder={clientId ? "Overstyr kundenavn (valgfritt)" : "Kundenavn på rapporten (valgfritt)"}
        className="flex-1 min-w-[180px] bg-surface border border-edge px-2 py-1.5 font-term text-[12px] text-coolwhite outline-none focus:border-nettred"
      />
      <button data-testid="diff-download-pdf" onClick={downloadPdf} disabled={!!busy || a === b}
        className={`${btn} bg-nettred border-nettred text-space hover:bg-amber hover:border-amber hover:text-space`}>
        {busy === "pdf" ? "Lager PDF…" : "Last ned PDF ↓"}
      </button>
      <button data-testid="diff-send-alert" onClick={sendAlert} disabled={!!busy}
        title="Sender siste uke mot forrige uke på e-post, med PDF vedlagt"
        className={`${btn} border-edge text-coolwhite hover:border-l3 hover:text-l3`}>
        {busy === "mail" ? "Sender…" : "Send AI-varsel nå ✉"}
      </button>
    </div>
  );
}

export const MentionsDiff = ({ ax, refreshKey, clientId }) => {
  const [batches, setBatches] = useState([]);
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const [diff, setDiff] = useState(null);

  useEffect(() => {
    setDiff(null); setA(null); setB(null);
    ax.get("/admin/mentions/batches", { params: { client_id: clientId || undefined } }).then(({ data }) => {
      setBatches(data);
      if (data.length >= 2) { setA(data[data.length - 2].batch_id); setB(data[data.length - 1].batch_id); }
    }).catch(() => setBatches([]));
  }, [ax, refreshKey, clientId]);

  useEffect(() => {
    if (!a || !b) return;
    ax.get("/admin/mentions/diff", { params: { a, b, client_id: clientId || undefined } }).then(({ data }) => setDiff(data)).catch(() => setDiff(null));
  }, [ax, a, b]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = diff?.queries?.reduce((t, q) => ({ n: t.n + q.new.length, l: t.l + q.lost.length }), { n: 0, l: 0 });

  return (
    <div data-testid="mentions-diff" className="intel-panel p-5 mt-4" style={{ "--panel-accent": "#10b981", borderLeftColor: "#10b981" }}>
      <div className="font-display tracking-[0.2em] text-[11px] uppercase text-l3">// AI_VISIBILITY_SCORE · DIFF_VIEW</div>
      {batches.length < 2 ? (
        <p data-testid="diff-empty" className="mt-3 font-term text-[12.5px] text-dim">
          // trenger minst 2 ukentlige batcher for å sammenligne ({batches.length} så langt). Trykk «Kjør batch nå» eller vent til neste mandag.
        </p>
      ) : (
        <>
          <VisibilityChart batches={batches} />
          <div className="mt-5 flex items-center gap-2 flex-wrap font-term text-[12px] text-dim">
            <span>Sammenlign</span>
            <BatchSelect testid="diff-select-a" value={a} onChange={setA} batches={batches} />
            <span>→</span>
            <BatchSelect testid="diff-select-b" value={b} onChange={setB} batches={batches} />
            {totals && (
              <span data-testid="diff-totals" className="ml-auto">
                <span style={{ color: "#10b981" }}>+{totals.n} nye</span> · <span style={{ color: "#ff8a8a" }}>−{totals.l} tapt</span> domener
              </span>
            )}
          </div>
          {a === b && <div className="mt-3 font-term text-[12px] text-amber">// velg to forskjellige batcher</div>}
          <ReportActions ax={ax} a={a} b={b} clientId={clientId} />
          {diff?.queries?.map((q) => <QueryDiffRow key={q.query} q={q} />)}
        </>
      )}
    </div>
  );
};
